import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, FormHelperText, IconButton, Collapse } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import debounce from 'lodash/debounce';

import AuthContext from "./auth/Context";
import Files from "./Files";

// CSS tuỳ chỉnh cho thẻ sinh ra từ Markdown
const markdownStyles = {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: '0.75rem',
    color: 'text.secondary',
    '& p': { margin: '0 0 8px 0', '&:last-child': { margin: 0 } },
    '& code': { bgcolor: 'rgba(0,0,0,0.06)', p: '2px 4px', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.7rem' },
    '& pre': { bgcolor: 'rgba(0,0,0,0.06)', p: 1, borderRadius: 1, overflowX: 'auto', '& code': { bgcolor: 'transparent', p: 0 } },
    '& table': { borderCollapse: 'collapse', width: '100%', mb: 1 },
    '& th, & td': { border: '1px solid #ccc', p: 0.5 },
    '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
    '& ul, & ol': { margin: '0 0 8px 0', paddingLeft: '20px' }
};

const HtmlEditor = ({
    obj,
    onChange,
    onSave,
    data: initialData = "",
    disabled = false,
    readOnly = false,
    config: userConfig,
    style,
    placeholder,
    helperText,
    debounceTime = 500
}) => {
    const context = useContext(AuthContext);
    
    // States
    const [data, setData] = useState(initialData || "");
    const [editorComponent, setEditorComponent] = useState(null);
    const [isHelperExpanded, setIsHelperExpanded] = useState(false);
    
    // Refs
    const filesManagerRef = useRef(null);
    const editorRef = useRef(null);
    const contentRef = useRef(data); // Giữ nội dung thực tế mới nhất
    const configRef = useRef(null);

    const isReadOnly = disabled || readOnly;

    // 1. Đồng bộ props data từ cha xuống
    useEffect(() => {
        const safeData = initialData || "";
        setData(safeData);
        contentRef.current = safeData;
    }, [initialData]);

    // 2. TẠO DEBOUNCE CHO ONCHANGE (Sử dụng lodash.debounce)
    const debouncedOnChange = useCallback(
        debounce((newValue) => {
            if (onChange) {
                onChange({}, newValue);
            }
        }, debounceTime),
        [onChange, debounceTime]
    );

    // Dọn dẹp debounce khi unmount
    useEffect(() => {
        return () => {
            debouncedOnChange.cancel();
        };
    }, [debouncedOnChange]);

    // 3. Lazy load Jodit & Cấu hình Editor
    useEffect(() => {
        let isMounted = true;

        const initEditor = async () => {
            try {
                const { default: Editor } = await import("jodit-react");
                const { Jodit } = await import('jodit');

                const baseConfig = userConfig || {
                    minHeight: 200,
                    toolbarButtonSize: 'large',
                    buttonsXS: ['bold', 'italic', '|', 'ul', 'ol', '|'],
                    buttonsSM: ['bold', 'italic', '|', 'ul', 'ol', '|'],
                    buttons: [
                        'bold', 'italic',
                        '|', 'ul', 'ol',
                        '|', 'font', 'fontsize', 'brush', 'paragraph',
                        '|', 'image', 'video', 'table', 'link',
                        '|', 'left', 'center', 'right', 'justify',
                        '|', 'hr',
                        '|', 'source'
                    ],
                    extraButtons: ["uploadFile"]
                };

                if (placeholder) {
                    baseConfig.placeholder = placeholder;
                }

                // Custom Custom Control: uploadFile
                Jodit.defaultOptions.controls.uploadFile = {
                    name: 'Upload file',
                    iconURL: "https://www.kindpng.com/picc/m/261-2619141_cage-clipart-victorian-cloud-upload-icon-svg-hd.png",
                    exec: async (editor) => {
                        if (filesManagerRef.current) {
                            const file = await new Promise((resolve) => {
                                filesManagerRef.current.showDialogPicker(file => {
                                    if (file && !file.shared) {
                                        context.alert("File này cần được share trước khi chọn");
                                        resolve(null);
                                    } else {
                                        resolve(file);
                                    }
                                });
                            });

                            if (file) {
                                const url = `${context.config.server_url}/public/file/shared/${file._id}`;
                                const ext = ((file.file || {}).extension || "").toLowerCase();
                                if (["png", "jpg", "jpeg", "gif", "webp"].indexOf(ext) >= 0) {
                                    const newNode = editor.selection.j.createInside.element('img');
                                    newNode.setAttribute('src', url);
                                    newNode.setAttribute('width', "300");
                                    editor.selection.insertNode(newNode);
                                } else {
                                    const html = `<a href="${url}">${(file.file || {}).originalname || (file.file || {}).name}</a>`;
                                    editor.s.insertHTML(html);
                                }
                            }
                        }
                        return false;
                    }
                };

                // Custom Control: saveData
                if (onSave) {
                    baseConfig.extraButtons = baseConfig.extraButtons || [];
                    if (!baseConfig.extraButtons.includes("saveData")) {
                        baseConfig.extraButtons.push("saveData");
                    }

                    Jodit.defaultOptions.controls.saveData = {
                        name: 'Save',
                        iconURL: "/images/save.png",
                        exec: async () => {
                            const currentVal = contentRef.current;
                            setData(currentVal);
                            if (onChange) onChange({}, currentVal);
                            onSave(currentVal);
                            return false;
                        }
                    };
                }

                configRef.current = baseConfig;

                if (isMounted) {
                    setEditorComponent(() => Editor);
                }
            } catch (error) {
                console.error("Lỗi khi load Jodit Editor:", error);
            }
        };

        initEditor();

        return () => {
            isMounted = false;
        };
    }, [userConfig, placeholder, onSave, context]);

    // 4. Xử lý gõ văn bản
    const handleContentChange = useCallback((newContent) => {
        if (isReadOnly) return;
        contentRef.current = newContent;
        // Bắn sự kiện onChange đã debounce lên cha
        debouncedOnChange(newContent);
    }, [isReadOnly, debouncedOnChange]);

    // 5. Xử lý khi OnBlur (Đảm bảo bắn dữ liệu tức thì khi user nhấp ra ngoài)
    const handleBlur = useCallback((newContent) => {
        if (isReadOnly) return;
        debouncedOnChange.cancel(); // Hủy đếm ngược
        setData(newContent);
        contentRef.current = newContent;
        if (onChange) {
            onChange({}, newContent);
        }
    }, [isReadOnly, onChange, debouncedOnChange]);

    if (!editorComponent) return null;

    const Editor = editorComponent;

    if (obj && !obj._id) {
        obj.__id_tmp__ = obj.__id_tmp__ || `__tmp__${new Date().getTime()}`;
    }

    // Process HelperText
    const helperLines = helperText ? String(helperText).split('\n') : [];
    const isMultiLineHelper = helperLines.length > 1;
    const firstLine = helperLines.length > 0 ? helperLines[0] : '';

    return (
        <div style={style}>
            <Editor
                ref={editorRef}
                value={data}
                disabled={isReadOnly}
                readOnly={isReadOnly}
                placeholder={placeholder}
                config={configRef.current}
                tabIndex={1}
                style={{ backgroundColor: "white" }}
                onBlur={handleBlur}
                onChange={handleContentChange}
            />

            {obj && configRef.current && configRef.current.extraButtons && configRef.current.extraButtons.indexOf("uploadFile") >= 0 && (
                <Files
                    isDialogPicker={true}
                    ref={filesManagerRef}
                    onFilesChanged={files => {
                        if (!obj._id) {
                            obj.exfields = (obj.exfields || {}).__files__ = files;
                        }
                    }}
                    idLink={obj._id || obj.__id_tmp__}
                    shared={true}
                    typeView="list"
                />
            )}

            {/* Render HelperText Markdown */}
            {helperText && (
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        {!isMultiLineHelper ? (
                            <FormHelperText component="div" disabled={isReadOnly} sx={{ mt: 0, mb: 0 }}>
                                <Box sx={markdownStyles}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{helperText}</ReactMarkdown>
                                </Box>
                            </FormHelperText>
                        ) : (
                            <>
                                {!isHelperExpanded && (
                                    <FormHelperText component="div" disabled={isReadOnly} sx={{ mt: 0, mb: 0, color: 'text.secondary', fontWeight: 500 }}>
                                        {firstLine} ...
                                    </FormHelperText>
                                )}
                                <Collapse in={isHelperExpanded} unmountOnExit>
                                    <FormHelperText
                                        component="div"
                                        disabled={isReadOnly}
                                        sx={{
                                            mt: 0.5,
                                            bgcolor: 'rgba(0,0,0,0.03)',
                                            p: 1,
                                            borderRadius: 1,
                                            border: '1px dashed #ccc'
                                        }}
                                    >
                                        <Box sx={markdownStyles}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{helperText}</ReactMarkdown>
                                        </Box>
                                    </FormHelperText>
                                </Collapse>
                            </>
                        )}
                    </Box>

                    {isMultiLineHelper && (
                        <IconButton
                            size="small"
                            onClick={() => setIsHelperExpanded(prev => !prev)}
                            sx={{ mt: -0.5, ml: 1, color: 'primary.main', bgcolor: 'rgba(25, 118, 210, 0.08)' }}
                        >
                            {isHelperExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                    )}
                </Box>
            )}
        </div>
    );
};

HtmlEditor.propTypes = {
    obj: PropTypes.any,
    onChange: PropTypes.func,
    onSave: PropTypes.func,
    data: PropTypes.string,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    config: PropTypes.any,
    style: PropTypes.any,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    debounceTime: PropTypes.number
};

export default HtmlEditor;